import React from 'react';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';

const CookiesPage = () => {
  const lastUpdated = 'January 1, 2026';

  return (
    <>
      <Helmet>
        <title>Cookie Policy - CalcBuildAI</title>
        <meta name="description" content="Cookie Policy for CalcBuildAI. Learn about the cookies we use and how to manage them." />
      </Helmet>

      <div className="min-h-screen bg-white py-16">
        <div className="max-w-3xl mx-auto px-4">
          <h1 className="text-4xl font-bold text-gray-900 mb-4">Cookie Policy</h1>
          <p className="text-gray-500 mb-8">Last updated: {lastUpdated}</p>

          <div className="prose prose-gray max-w-none">
            <p className="lead">
              This Cookie Policy explains how CalcBuildAI uses cookies and 
              similar technologies when you visit our website.
            </p>

            <h2>1. What Are Cookies?</h2>
            <p>
              Cookies are small text files stored on your device when you visit a website. 
              They help websites remember your preferences, understand how you use the site, 
              and provide a better experience.
            </p>

            <h2>2. Types of Cookies We Use</h2>
            
            <h3>2.1 Essential Cookies</h3>
            <p>
              These cookies are necessary for the website to function properly. They enable 
              core features like authentication, security, and accessibility.
            </p>
            <table className="w-full">
              <thead>
                <tr>
                  <th>Cookie</th>
                  <th>Purpose</th>
                  <th>Duration</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td>sb-auth-token</td>
                  <td>Authentication session</td>
                  <td>Session / 1 week</td>
                </tr>
                <tr>
                  <td>device_id</td>
                  <td>Anonymous usage tracking</td>
                  <td>Persistent</td>
                </tr>
              </tbody>
            </table>

            <h3>2.2 Functional Cookies</h3>
            <p>
              These cookies enable enhanced functionality and personalization, such as 
              remembering your preferences and settings.
            </p>
            <table className="w-full">
              <thead>
                <tr>
                  <th>Cookie</th>
                  <th>Purpose</th>
                  <th>Duration</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td>theme_preference</td>
                  <td>Remember light/dark mode preference</td>
                  <td>1 year</td>
                </tr>
                <tr>
                  <td>units_preference</td>
                  <td>Remember imperial/metric preference</td>
                  <td>1 year</td>
                </tr>
              </tbody>
            </table>

            <h3>2.3 Analytics Cookies</h3>
            <p>
              These cookies help us understand how visitors interact with our website, 
              allowing us to improve the user experience.
            </p>
            <table className="w-full">
              <thead>
                <tr>
                  <th>Cookie</th>
                  <th>Purpose</th>
                  <th>Duration</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td>_ga</td>
                  <td>Google Analytics - distinguishes users</td>
                  <td>2 years</td>
                </tr>
                <tr>
                  <td>_ga_*</td>
                  <td>Google Analytics - persists session state</td>
                  <td>2 years</td>
                </tr>
              </tbody>
            </table>

            <h3>2.4 Advertising Cookies (Free Tier Only)</h3>
            <p>
              For free users, we may display advertisements. These cookies are used by 
              our advertising partners to show relevant ads.
            </p>
            <table className="w-full">
              <thead>
                <tr>
                  <th>Provider</th>
                  <th>Purpose</th>
                  <th>More Info</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td>Google AdSense</td>
                  <td>Display relevant advertisements</td>
                  <td><a href="https://policies.google.com/privacy" target="_blank" rel="noopener noreferrer">Google Privacy Policy</a></td>
                </tr>
              </tbody>
            </table>

            <h2>3. Third-Party Cookies</h2>
            <p>We use services from third parties that may set their own cookies:</p>
            <ul>
              <li><strong>Supabase:</strong> Authentication and data storage</li>
              <li><strong>Stripe:</strong> Payment processing</li>
              <li><strong>Google Analytics:</strong> Website analytics</li>
              <li><strong>Google AdSense:</strong> Advertising (free tier only)</li>
            </ul>

            <h2>4. Managing Cookies</h2>
            
            <h3>4.1 Browser Settings</h3>
            <p>
              Most browsers allow you to manage cookie preferences. You can typically:
            </p>
            <ul>
              <li>View what cookies are stored on your device</li>
              <li>Delete individual or all cookies</li>
              <li>Block cookies from specific or all websites</li>
              <li>Set preferences for specific types of cookies</li>
            </ul>
            <p>
              Note that blocking essential cookies may prevent our website from functioning 
              properly.
            </p>

            <h3>4.2 Browser-Specific Instructions</h3>
            <ul>
              <li><a href="https://support.google.com/chrome/answer/95647" target="_blank" rel="noopener noreferrer">Google Chrome</a></li>
              <li><a href="https://support.mozilla.org/en-US/kb/clear-cookies-and-site-data-firefox" target="_blank" rel="noopener noreferrer">Mozilla Firefox</a></li>
              <li><a href="https://support.apple.com/guide/safari/manage-cookies-sfri11471/mac" target="_blank" rel="noopener noreferrer">Safari</a></li>
              <li><a href="https://support.microsoft.com/en-us/windows/manage-cookies-in-microsoft-edge-view-allow-block-delete-and-use-168dab11-0753-043d-7c16-ede5947fc64d" target="_blank" rel="noopener noreferrer">Microsoft Edge</a></li>
            </ul>

            <h3>4.3 Opt-Out Options</h3>
            <ul>
              <li>
                <strong>Google Analytics:</strong>{' '}
                <a href="https://tools.google.com/dlpage/gaoptout" target="_blank" rel="noopener noreferrer">
                  Google Analytics Opt-out Browser Add-on
                </a>
              </li>
              <li>
                <strong>Advertising:</strong>{' '}
                <a href="https://optout.aboutads.info/" target="_blank" rel="noopener noreferrer">
                  Digital Advertising Alliance Opt-Out
                </a>
              </li>
            </ul>

            <h2>5. Do Not Track</h2>
            <p>
              Some browsers have a "Do Not Track" feature. We currently do not respond to 
              Do Not Track signals, but you can use the cookie management options described 
              above.
            </p>

            <h2>6. Updates to This Policy</h2>
            <p>
              We may update this Cookie Policy from time to time. Changes will be posted 
              on this page with an updated "Last updated" date.
            </p>

            <h2>7. Contact Us</h2>
            <p>
              If you have questions about our use of cookies, please contact us at{' '}
              <a href="mailto:privacy@calcbuildai.com">privacy@calcbuildai.com</a> or 
              visit our <Link to="/contact">Contact Page</Link>.
            </p>
          </div>
        </div>
      </div>
    </>
  );
};

export default CookiesPage;
